import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Pie, Bar, Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import Sidenav from "../components/Sidenav";
import "../Console.css";

Chart.register(...registerables);

const BRAND_PALETTE = [
  "#ff5100",
  "#ff8c5a",
  "#c13d00",
  "#ffb38a",
  "#374151",
  "#6b7280",
  "#9ca3af",
  "#d1d5db",
  "#1d4ed8",
  "#60a5fa",
  "#059669",
  "#34d399",
];

const Analytics = ({ domain, userData }) => {
  const navigate = useNavigate();
  const [postsPerDay, setPostsPerDay] = useState([]);
  const [usersPerDay, setUsersPerDay] = useState([]);
  const [postsByCategory, setPostsByCategory] = useState([]);
  const [postsByCity, setPostsByCity] = useState([]);

  const fetchPostsPerDay = () => {
    fetch("/api/posts/postsperday")
      .then((r) => r.json())
      .then(setPostsPerDay)
      .catch((e) => console.error("Error fetching data:", e));
  };

  const fetchUsersPerDay = () => {
    fetch("/api/posts/usersperday")
      .then((r) => r.json())
      .then(setUsersPerDay)
      .catch((e) => console.error("Error fetching data:", e));
  };

  const fetchPostsByCategory = () => {
    fetch("/api/posts/postsbycategory")
      .then((r) => r.json())
      .then(setPostsByCategory)
      .catch((e) => console.error("Error fetching data:", e));
  };

  const fetchPostsByCity = () => {
    fetch("/api/posts/postsbycity")
      .then((r) => r.json())
      .then((data) =>
        setPostsByCity(data.filter((item) => item.city !== "Total")),
      )
      .catch((e) => console.error("Error fetching data:", e));
  };

  useEffect(() => {
    fetchPostsPerDay();
    fetchUsersPerDay();
    fetchPostsByCategory();
    fetchPostsByCity();
  }, []);

  const filteredCategories = postsByCategory.filter(
    (item) => item.category !== "Total",
  );
  const totalCount =
    postsByCategory.find((item) => item.category === "Total")?.totalCount || 0;
  const totalUsers = usersPerDay.reduce((sum, d) => sum + (d.count || 0), 0);
  const totalDays = postsPerDay.length;
  const avgPerDay = totalDays > 0 ? Math.round(totalCount / totalDays) : 0;

  const categoryChartData = {
    labels: filteredCategories.map((item) => item.category),
    datasets: [
      {
        data: filteredCategories.map((item) => item.totalCount),
        backgroundColor: BRAND_PALETTE,
        hoverBackgroundColor: BRAND_PALETTE,
        borderWidth: 2,
        borderColor: "#fff",
      },
    ],
  };

  const cityChartData = {
    labels: postsByCity.map((item) => item.city || "Unknown"),
    datasets: [
      {
        label: "Total Posts",
        data: postsByCity.map((item) => item.totalCount),
        backgroundColor: BRAND_PALETTE,
        borderWidth: 2,
        borderColor: "#fff",
      },
    ],
  };

  const postsPerDayChartData = {
    labels: postsPerDay.map((item) => item.date),
    datasets: [
      {
        label: "Bleeps Per Day",
        data: postsPerDay.map((item) => item.count),
        fill: true,
        borderColor: "#ff5100",
        backgroundColor: "rgba(255,81,0,0.08)",
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: "#ff5100",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
      },
    ],
  };

  const usersPerDayChartData = {
    labels: usersPerDay.map((item) => item.date),
    datasets: [
      {
        label: "New Users Per Day",
        data: usersPerDay.map((item) => item.count),
        fill: true,
        borderColor: "#1d4ed8",
        backgroundColor: "rgba(29,78,216,0.06)",
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: "#1d4ed8",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
      },
    ],
  };

  const lineChartOptions = (yLabel, color) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#111827",
        titleColor: "#f9fafb",
        bodyColor: "#d1d5db",
        borderColor: color,
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 7,
          color: "#9ca3af",
          font: { size: 11 },
        },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: { color: "#9ca3af", font: { size: 11 } },
        border: { display: false },
      },
    },
  });

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 16,
          font: { size: 11 },
          color: "#374151",
          boxWidth: 12,
          boxHeight: 12,
          borderRadius: 4,
        },
      },
      tooltip: {
        backgroundColor: "#111827",
        titleColor: "#f9fafb",
        bodyColor: "#d1d5db",
        borderColor: "rgba(255,81,0,0.3)",
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
      },
    },
  };

  const statCards = [
    {
      icon: "fa-bolt",
      label: "Total Bleeps",
      value: totalCount.toLocaleString(),
      color: "#ff5100",
      bg: "rgba(255,81,0,0.08)",
      border: "rgba(255,81,0,0.2)",
    },
    {
      icon: "fa-users",
      label: "New Users (tracked)",
      value: totalUsers.toLocaleString(),
      color: "#1d4ed8",
      bg: "rgba(29,78,216,0.07)",
      border: "rgba(29,78,216,0.2)",
    },
    {
      icon: "fa-layer-group",
      label: "Categories",
      value: filteredCategories.length,
      color: "#059669",
      bg: "rgba(5,150,105,0.07)",
      border: "rgba(5,150,105,0.2)",
    },
    {
      icon: "fa-chart-line",
      label: "Avg Bleeps / Day",
      value: avgPerDay.toLocaleString(),
      color: "#7c3aed",
      bg: "rgba(124,58,237,0.07)",
      border: "rgba(124,58,237,0.2)",
    },
  ];

  return (
    <>
      <style>{`
        .analytics-stat-card {
          background: #fff;
          border-radius: 16px;
          border: 1px solid #f3f4f6;
          padding: 20px 22px;
          display: flex;
          align-items: center;
          gap: 16px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.05);
          flex: 1 1 200px;
          min-width: 0;
        }
        .analytics-stat-icon {
          width: 46px;
          height: 46px;
          border-radius: 13px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }
        .analytics-stat-value {
          font-size: 26px;
          font-weight: 800;
          color: #111827;
          line-height: 1;
        }
        .analytics-stat-label {
          font-size: 12px;
          font-weight: 600;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          margin-top: 4px;
        }
        .analytics-chart-card {
          background: #fff;
          border-radius: 18px;
          border: 1px solid #f3f4f6;
          box-shadow: 0 1px 4px rgba(0,0,0,0.05);
          overflow: hidden;
        }
        .analytics-chart-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 18px 22px 14px;
          border-bottom: 1px solid #f9fafb;
        }
        .analytics-chart-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: rgba(255,81,0,0.08);
          border: 1.5px solid rgba(255,81,0,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .analytics-chart-title {
          font-size: 15px;
          font-weight: 800;
          color: #111827;
          line-height: 1.2;
        }
        .analytics-chart-subtitle {
          font-size: 12px;
          color: #9ca3af;
          margin-top: 2px;
        }
        .analytics-chart-body {
          padding: 20px 22px 22px;
        }
      `}</style>

      <Helmet>
        <title>Analytics | BLEEP</title>
        <meta
          name="description"
          content="Introducing BLEEP, the revolutionary crowdsourcing app designed to combat counterfeit products and services."
        />
      </Helmet>

      <div className="d-flex">
        <Sidenav domain={domain} />
        <div
          style={{
            flex: 1,
            position: "relative",
            height: "calc(100vh - 94px)",
            overflowY: "auto",
          }}
        >
          {/* Header */}
          <header
            style={{
              position: "relative",
              paddingInline: "30px",
              paddingBlock: "30px",
              color: "#fff",
              background:
                "linear-gradient(135deg, rgba(255,81,0,1) 0%, rgba(255,0,0,1) 100%)",
              overflow: "hidden",
            }}
          >
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: "url('/images/background-art.jpg')",
                backgroundRepeat: "repeat",
                backgroundSize: "400px 400px",
                backgroundPosition: "center",
                mixBlendMode: "overlay",
                opacity: 0.1,
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
            <div
              className="row d-flex align-items-center"
              style={{ position: "relative", zIndex: 1 }}
            >
              <div
                className="col"
                style={{ padding: 10 }}
              >
                <div
                  className="col d-flex align-items-center"
                  style={{ color: "white", flexDirection: "row" }}
                >
                  <div>
                    <b
                      style={{
                        fontSize: "30px",
                        textShadow: "#1c1c1c 1px 2px 0px",
                      }}
                    >
                      <i className="fas fa-chart-line" /> App-wide Analytics
                    </b>
                  </div>
                </div>
                <p
                  style={{
                    paddingTop: "10px",
                    textShadow: "#1c1c1c 1px 1px 0px",
                  }}
                >
                  Visualize and analyze BLEEP usage trends
                </p>
              </div>
            </div>
          </header>

          {/* Body */}
          <div
            style={{
              background: "#f3f4f6",
              padding: "24px",
              minHeight: "calc(100% - 130px)",
            }}
          >
            {/* Stat Cards */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 14,
                marginBottom: 24,
              }}
            >
              {statCards.map((s) => (
                <div
                  className="analytics-stat-card"
                  key={s.label}
                >
                  <div
                    className="analytics-stat-icon"
                    style={{
                      background: s.bg,
                      border: `1.5px solid ${s.border}`,
                      color: s.color,
                    }}
                  >
                    <i className={`fas ${s.icon}`} />
                  </div>
                  <div>
                    <div className="analytics-stat-value">{s.value}</div>
                    <div className="analytics-stat-label">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pie Charts Row */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 16,
                marginBottom: 16,
              }}
            >
              <div
                className="analytics-chart-card"
                style={{ flex: "1 1 340px", minWidth: 0 }}
              >
                <div className="analytics-chart-header">
                  <div className="analytics-chart-icon">
                    <i
                      className="fas fa-chart-pie"
                      style={{ color: "#ff5100", fontSize: 14 }}
                    />
                  </div>
                  <div>
                    <div className="analytics-chart-title">
                      Bleeps by Category
                    </div>
                    <div className="analytics-chart-subtitle">
                      Distribution across all report types
                    </div>
                  </div>
                </div>
                <div
                  className="analytics-chart-body"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 16,
                  }}
                >
                  <div style={{ width: "100%", maxWidth: 280, height: 280 }}>
                    <Pie
                      data={categoryChartData}
                      options={pieChartOptions}
                    />
                  </div>
                </div>
              </div>

              <div
                className="analytics-chart-card"
                style={{ flex: "1 1 340px", minWidth: 0 }}
              >
                <div className="analytics-chart-header">
                  <div className="analytics-chart-icon">
                    <i
                      className="fas fa-location-dot"
                      style={{ color: "#ff5100", fontSize: 14 }}
                    />
                  </div>
                  <div>
                    <div className="analytics-chart-title">Bleeps by City</div>
                    <div className="analytics-chart-subtitle">
                      Geographic spread of reports
                    </div>
                  </div>
                </div>
                <div
                  className="analytics-chart-body"
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <div style={{ width: "100%", maxWidth: 280, height: 280 }}>
                    <Pie
                      data={cityChartData}
                      options={pieChartOptions}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Bleep Trends */}
            <div
              className="analytics-chart-card"
              style={{ marginBottom: 16 }}
            >
              <div className="analytics-chart-header">
                <div className="analytics-chart-icon">
                  <i
                    className="fas fa-chart-area"
                    style={{ color: "#ff5100", fontSize: 14 }}
                  />
                </div>
                <div>
                  <div className="analytics-chart-title">
                    Bleep Trends Over Time
                  </div>
                  <div className="analytics-chart-subtitle">
                    Daily report volume — rolling timeline
                  </div>
                </div>
              </div>
              <div className="analytics-chart-body">
                <div style={{ height: 340 }}>
                  <Line
                    data={postsPerDayChartData}
                    options={lineChartOptions("Posts per day", "#ff5100")}
                  />
                </div>
              </div>
            </div>

            {/* User Acquisition */}
            <div className="analytics-chart-card">
              <div className="analytics-chart-header">
                <div
                  className="analytics-chart-icon"
                  style={{
                    background: "rgba(29,78,216,0.07)",
                    border: "1.5px solid rgba(29,78,216,0.2)",
                  }}
                >
                  <i
                    className="fas fa-user-plus"
                    style={{ color: "#1d4ed8", fontSize: 14 }}
                  />
                </div>
                <div>
                  <div className="analytics-chart-title">
                    User Acquisition Over Time
                  </div>
                  <div className="analytics-chart-subtitle">
                    New accounts registered per day
                  </div>
                </div>
              </div>
              <div className="analytics-chart-body">
                <div style={{ height: 340 }}>
                  <Line
                    data={usersPerDayChartData}
                    options={lineChartOptions("Users per day", "#1d4ed8")}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Analytics;
